import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { draftRecipeLink } from '../../../routers'
import TrashButton from '../../../components/TrashButton'
import UserPhoto from '../../../components/UserPhoto'
import { addedByLine, creditName } from '../../../utils/recipeCredit'

const NO_PHOTO = '/images/imageRecipeWithoutPhoto.png'

// Wider than this counts as a landscape photo and gets the full-width banner
const WIDE_RATIO = 1.3

const usePhotoShape = (src) => {
  const [shape, setShape] = useState(null)

  useEffect(() => {
    setShape(null)
    if (!src) return undefined
    let live = true
    const img = new Image()
    img.onload = () =>
      live && setShape(img.naturalWidth / img.naturalHeight >= WIDE_RATIO ? 'wide' : 'tall')
    img.onerror = () => live && setShape('wide')
    img.src = src
    return () => {
      live = false
    }
  }, [src])

  return shape
}

// The family member who added a recipe for its owner can edit it; only the owner deletes it
const Owner = ({ recipe, canEdit, canDelete, onDelete }) =>
  canEdit ? (
    <div className="recipeHeaderSetting" onClick={(e) => e.stopPropagation()}>
      <Link to={draftRecipeLink(recipe?.id, 1)} className="opacity">
        Edit recipe
      </Link>
      {canDelete ? (
        <>
          &nbsp;|&nbsp;
          <TrashButton deleteRecipe={onDelete} />
        </>
      ) : null}
    </div>
  ) : null

// "Recipe by Pat" (and "Added by Sam" when Sam added it for Pat), or
// "Grandma Rose's recipe, added by Sam" for someone who isn't on Morsel
const Byline = ({ recipe }) => {
  const added = addedByLine(recipe)
  if (recipe?.credited_name) {
    return (
      <span>
        <strong>{recipe.credited_name}</strong>'s recipe
        {added ? `, ${added.charAt(0).toLowerCase()}${added.slice(1)}` : ''}
      </span>
    )
  }
  return (
    <span>
      Recipe by <strong>{recipe?.author?.fullname}</strong>
      {added ? <span className="recipeAddedBy">{added}</span> : null}
    </span>
  )
}

const Thumbs = ({ photos, current, onOpen }) =>
  photos.length > 1 ? (
    <div className="recipeHeroThumbs" onClick={(e) => e.stopPropagation()}>
      {photos.map((photo, index) => (
        <button
          type="button"
          key={photo.image + index}
          className={`recipeHeroThumb ${index === current ? 'current' : ''} ${
            photo.kind === 'card' ? 'isCard' : ''
          }`}
          onClick={() => onOpen(index)}
          aria-label={`Open ${photo.label.toLowerCase()}`}
        >
          <img src={photo.image} alt="" />
        </button>
      ))}
    </div>
  ) : null

// Top of the recipe page. A landscape food photo fills a banner with the title on it.
// A portrait or square photo, or a scanned card with no food photo, shows whole beside the title.
const RecipeHero = ({ recipe, photos, canEdit, canDelete, onDelete, onOpen }) => {
  const hero = photos[0]
  const shape = usePhotoShape(hero?.image)
  const split = hero && (hero.kind === 'card' || shape === 'tall')

  if (split) {
    return (
      <div className={`recipeHeroSplit ${hero.kind === 'card' ? 'heroIsCard' : ''}`}>
        <div className="recipeHeroText">
          <p className="recipeHeaderTitle">{recipe.name}</p>
          <div className="recipeHeroByline">
            <UserPhoto classStylePhoto="recipeHeroAvatar" image={recipe?.author?.avatar} />
            <Byline recipe={recipe} />
          </div>
          <Thumbs photos={photos} current={0} onOpen={onOpen} />
          <Owner recipe={recipe} canEdit={canEdit} canDelete={canDelete} onDelete={onDelete} />
        </div>
        <button type="button" className="recipeHeroPhoto" onClick={() => onOpen(0)}>
          <img src={hero.image} alt={hero.label} />
          {hero.kind === 'card' && <span className="recipeHeroPhotoTag">Original recipe card</span>}
        </button>
      </div>
    )
  }

  return (
    <div
      onClick={() => hero && onOpen(0)}
      style={{ backgroundImage: `url(${hero ? hero.image : NO_PHOTO})` }}
      className={`backgroundRecipeStyle ${hero ? '' : 'noPhoto'}`}
    >
      <div className="backgroundShadowRecipe" />
      <div className="recipeHeader">
        <div className="recipeHeaderMain">
          <p className="recipeHeaderTitle">{recipe.name}</p>
          <Thumbs photos={photos} current={0} onOpen={onOpen} />
        </div>
        <Owner recipe={recipe} canEdit={canEdit} canDelete={canDelete} onDelete={onDelete} />
      </div>
      <div className="innerInfoCreatorUser">
        <span className="infoCreatorUserText">Recipe by:</span>
        <p className="infoCreatorUser">
          {creditName(recipe)}
          {addedByLine(recipe) ? (
            <span className="recipeAddedBy">{addedByLine(recipe)}</span>
          ) : null}
        </p>
        <UserPhoto classStylePhoto="infoCreatorUserPhoto" image={recipe?.author?.avatar} />
      </div>
    </div>
  )
}

export default RecipeHero
